import { Mesh } from "./basePass";
import { Shader } from "./core/shaderUtils";
import { GStack, GetVec3, Vector3 } from "./core/types";
import { MathGetVec3Length } from "./core/utils";
import { GLBindUniformBuffer, GLSetTexture, GLSetVec3 } from "./helpers/glHelper";
import { SceneBase } from "./scenes/scene";
import { CommonRenderingResources } from "./shaders/shaderConfig";
import { ShaderSourceFullscreenPassVS } from "./shaders/shaderPostProcess";
import {
    GetShaderSourceMeshToSDFConverterPS,
    GetShaderSourceMeshVisualizerPS,
    GetShaderSourceMeshVisualizerVS,
    GetShaderSourceVolumeTextureRenderPS,
} from "./shaders/shaderSDFMeshGenerator";

function GetUniformParametersList(gl: WebGL2RenderingContext, shaderProgram: WebGLProgram) {
    const params = {
        LayerIndex: gl.getUniformLocation(shaderProgram, "LayerIndex"),
        NumTriangles: gl.getUniformLocation(shaderProgram, "NumTriangles"),
        VertexBufferTexture: gl.getUniformLocation(shaderProgram, "VertexBufferTexture"),
        SDFTextureSize: gl.getUniformLocation(shaderProgram, "SDFTextureSize"),
        SDFMeshExtent: gl.getUniformLocation(shaderProgram, "SDFMeshExtent"),

        SceneUniformBuffer: gl.getUniformBlockIndex(shaderProgram, "SceneUniformBuffer"),
    };
    return params;
}

export class GSDFMeshGenerator {
    static MeshVisualizerShader: Shader;
    static VertexBufferTexture: WebGLTexture;
    static NumVerts: number;
    static bMeshVisualizerInit = false;

    static kTextureSize = 64;
    static kTextureSize3D = new Vector3(
        GSDFMeshGenerator.kTextureSize,
        GSDFMeshGenerator.kTextureSize,
        GSDFMeshGenerator.kTextureSize,
    );

    static InitMeshVisualizer(gl: WebGL2RenderingContext, pMesh: Mesh) {
        if (!this.bMeshVisualizerInit) {
            GSDFMeshGenerator.MeshVisualizerShader = new Shader(
                gl,
                GetShaderSourceMeshVisualizerVS(),
                GetShaderSourceMeshVisualizerPS(),
                GetUniformParametersList,
            );

            //Upload Vertices to texture
            {
                GSDFMeshGenerator.VertexBufferTexture = gl.createTexture()!;
                //Upload Vertices to texture
                {
                    this.UploadVertexBufferToTexture(
                        gl,
                        pMesh.GeometryBufferCPU!,
                        GSDFMeshGenerator.VertexBufferTexture!,
                    );
                }
            }

            GSDFMeshGenerator.NumVerts = pMesh.NumIndices > 0 ? pMesh.NumIndices : pMesh.NumVertices;

            this.bMeshVisualizerInit = true;
        }
    }

    static VisualizeMesh(gl: WebGL2RenderingContext, pMesh: Mesh) {
        if (!pMesh.bCanDraw) return;
        if (!this.bMeshVisualizerInit) {
            GSDFMeshGenerator.InitMeshVisualizer(gl, pMesh);
        }
        const curShader = GSDFMeshGenerator.MeshVisualizerShader;
        const uniformsLocation = curShader.UniformParametersLocationList;

        gl.useProgram(curShader.ShaderProgram);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformsLocation.SceneUniformBuffer,
        );

        GLSetTexture(gl, uniformsLocation.VertexBufferTexture, GSDFMeshGenerator.VertexBufferTexture, 1);

        gl.drawArrays(gl.TRIANGLES, 0, GSDFMeshGenerator.NumVerts);
    }

    static UploadVertexBufferToTexture(
        gl: WebGL2RenderingContext,
        vertexBufferCPU: Float32Array,
        vertexBufferTexture: WebGLTexture,
    ) {
        const numVertices = vertexBufferCPU.length;
        gl.bindTexture(gl.TEXTURE_2D, vertexBufferTexture);

        // Set texture parameters for float32 texture
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        // Allocate storage for the texture
        const rowSize = 450;
        const texWidth = numVertices < rowSize ? numVertices : rowSize;
        const texHeight = Math.floor((numVertices - 1) / rowSize) + 1;
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, texWidth, texHeight, 0, gl.RED, gl.FLOAT, vertexBufferCPU);
        //gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, texWidth, texHeight, 0, gl.RED, gl.FLOAT, null);

        /* gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, texWidth, texHeight - 1, gl.RED, gl.FLOAT, vertexBufferCPU);

        const dataRectSize = texWidth * (texHeight - 1);
        const pixelsRemain = numVertices - dataRectSize;
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, texWidth, texHeight - 1, gl.RED, gl.FLOAT, vertexBufferCPU); */
        //console.log(`Create VB Texture, VB Size: ${numVertices}, Tex Width : ${texWidth}, Tex Height: ${texHeight}`);
        //gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, numVertices, 1, 0, gl.RED, gl.FLOAT, vertexBufferCPU);
    }

    static CreateTexture3D(gl: WebGL2RenderingContext) {
        const texture3D = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_3D, texture3D);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        return texture3D;
    }

    static GenerateFromGeometry(
        gl: WebGL2RenderingContext,
        vertexBufferCPU: Float32Array,
        numVerts: number,
        meshBBoxExtent: Vector3,
    ) {
        const width = GSDFMeshGenerator.kTextureSize3D.x; // Texture width
        const height = GSDFMeshGenerator.kTextureSize3D.y; // Texture height
        const depth = GSDFMeshGenerator.kTextureSize3D.z; // Texture depth

        const texture3D = GSDFMeshGenerator.CreateTexture3D(gl);
        const textureInternalFormat = gl.R16F;
        const textureFormat = gl.RED;
        const textureType = gl.HALF_FLOAT;

        gl.texImage3D(
            gl.TEXTURE_3D,
            0,
            textureInternalFormat,
            width,
            height,
            depth,
            0,
            textureFormat,
            textureType,
            null,
        );

        const vertexBufferTexture = gl.createTexture();
        //Upload Vertices to texture
        {
            this.UploadVertexBufferToTexture(gl, vertexBufferCPU, vertexBufferTexture!);
        }

        //Render To Texture on GPU
        {
            {
                gl.bindTexture(gl.TEXTURE_2D, null);

                const fbo = gl.createFramebuffer();
                gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);

                {
                    const curShader = new Shader(
                        gl,
                        ShaderSourceFullscreenPassVS,
                        GetShaderSourceMeshToSDFConverterPS(),
                        GetUniformParametersList,
                    );
                    gl.viewport(0, 0, width, height);
                    gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);
                    gl.useProgram(curShader.ShaderProgram);

                    gl.uniform3i(
                        curShader.UniformParametersLocationList.SDFTextureSize,
                        GSDFMeshGenerator.kTextureSize3D.x,
                        GSDFMeshGenerator.kTextureSize3D.y,
                        GSDFMeshGenerator.kTextureSize3D.z,
                    );

                    //gl.uniform3i(curShader.UniformParametersLocationList.SDFMeshExtent, 1, 1, 1);
                    GLSetVec3(gl, curShader.UniformParametersLocationList.SDFMeshExtent, meshBBoxExtent);

                    const numLayers = depth;
                    for (let layer = 0; layer < numLayers; layer++) {
                        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
                        gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, texture3D, 0, layer);

                        gl.uniform1i(curShader.UniformParametersLocationList.LayerIndex, layer);
                        gl.uniform1i(curShader.UniformParametersLocationList.NumTriangles, Math.floor(numVerts / 3));
                        GLSetTexture(
                            gl,
                            curShader.UniformParametersLocationList.VertexBufferTexture,
                            vertexBufferTexture,
                            1,
                        );

                        gl.drawArrays(gl.TRIANGLES, 0, 3);
                    }
                    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
                    gl.bindTexture(gl.TEXTURE_2D, null);
                }
            }
        }

        return texture3D;
    }

    static Generate(gl: WebGL2RenderingContext) {
        const meshSize = 1.0;
        const meshExtent = GetVec3(meshSize, meshSize, meshSize);
        const meshMin = GetVec3(-meshSize, -meshSize, -meshSize);
        const texPrec = 64;
        const textureSize3D = GetVec3(texPrec, texPrec, 4);
        const voxelSize = GetVec3(0, 0, 0);
        voxelSize.x = (meshExtent.x * 2.0) / textureSize3D.x;
        voxelSize.y = (meshExtent.y * 2.0) / textureSize3D.y;
        voxelSize.z = (meshExtent.z * 2.0) / textureSize3D.z;

        const sphereRadius = 0.5;

        //Create 3D texture
        {
            const texture3D = GSDFMeshGenerator.CreateTexture3D(gl);

            const width = textureSize3D.x; // Texture width
            const height = textureSize3D.y; // Texture height
            const depth = textureSize3D.z; // Texture depth

            // Example: Create a simple checkerboard pattern
            //const data = new Float32Array(width * height * depth * 1); // RGBA values
            const data = new Uint8Array(width * height * depth * 4); // RGBA values
            const stride = 4;

            if (0) {
                //upload data
                for (let z = 0; z < depth; z++) {
                    for (let y = 0; y < height; y++) {
                        for (let x = 0; x < width; x++) {
                            const index = (z * width * height + y * width + x) * stride;
                            const curVoxelPos = GStack.TempVec3;
                            curVoxelPos.x = meshMin.x + voxelSize.x * x + voxelSize.x * 0.5;
                            curVoxelPos.y = meshMin.y + voxelSize.y * y + voxelSize.y * 0.5;
                            curVoxelPos.z = meshMin.z + voxelSize.z * z + voxelSize.z * 0.5;
                            const sdf = MathGetVec3Length(curVoxelPos) - sphereRadius;
                            // data[index] = sdf;
                            //data[index] = 0.0;
                            let value = 0.0;
                            if (sdf < 0.1) {
                                value = 255.0;
                            }
                            data[index] = value; // Red
                            data[index + 1] = 255; // Green
                            data[index + 2] = 255; // Blue
                            data[index + 3] = 255; // Alpha
                        }
                    }
                }

                //const dataUnorm = new Uint16Array(data.buffer);

                // Upload the texture data
                gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGBA, width, height, depth, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
            } else {
                const textureInternalFormat = gl.R16F;
                const textureFormat = gl.RED;
                const textureType = gl.HALF_FLOAT;

                gl.texImage3D(
                    gl.TEXTURE_3D,
                    0,
                    textureInternalFormat,
                    width,
                    height,
                    depth,
                    0,
                    textureFormat,
                    textureType,
                    null,
                );

                //Render To Texture on GPU
                {
                    const fbo = gl.createFramebuffer();
                    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);

                    {
                        const curShader = new Shader(
                            gl,
                            ShaderSourceFullscreenPassVS,
                            GetShaderSourceVolumeTextureRenderPS(),
                            GetUniformParametersList,
                        );
                        gl.viewport(0, 0, width, height);
                        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);
                        gl.useProgram(curShader.ShaderProgram);

                        const numLayers = depth;
                        for (let layer = 0; layer < numLayers; layer++) {
                            gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
                            gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, texture3D, 0, layer);

                            gl.uniform1i(curShader.UniformParametersLocationList.LayerIndex, layer);

                            gl.drawArrays(gl.TRIANGLES, 0, 3);
                        }
                    }
                }
            }

            return texture3D;
        }
    }
}
